import { useState, useEffect } from "react";
import logo from "../../assets/images/logo/logo-ima-hec.png";
import { useNavigate, Link } from "react-router-dom";
import { loginUser } from "../../api/auth";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import CryptoJS from "crypto-js";
import { registerSocketUser } from "../../utils/socket";
import { DESKTOP_UNREAD_REMINDER_KEY } from "../../constants/desktopUnreadReminder";
import { getLatestVersion } from "../../api/system/appVersions";
import { clearLessonCacheSessionKey } from "../../utils/secureLessonDocumentCache";
import { getOrCreateDeviceFingerprint, getDeviceClass } from "../../utils/deviceFingerprint";
import AuthShell, {
  authInputWithIconClass,
  authPrimaryBtnClass,
} from "./AuthShell";

const SECRET_KEY = "IMA_SECRET_KEY_2025"; // ⚠️ Bạn có thể đổi sang secret khác

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  /* ---------------------------------
     🔥 AUTO LOGIN nếu có token
  --------------------------------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/hrm/dashboard");
    }
  }, []);

  /* ---------------------------------
     📱 Kiểm tra mobile UI
  --------------------------------- */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------------------------------
     🔄 Load dữ liệu Remember Me
  --------------------------------- */
  useEffect(() => {
    const savedEmail = localStorage.getItem("remember_email");
    const savedEncryptedPass = localStorage.getItem("remember_password");

    if (savedEmail && savedEncryptedPass) {
      const decryptedPass = CryptoJS.AES.decrypt(savedEncryptedPass, SECRET_KEY)
        .toString(CryptoJS.enc.Utf8);

      setEmail(savedEmail);
      setPassword(decryptedPass);
      setRememberMe(true);
    }
  }, []);

  // =======================================================
  // 🔹 Load phiên bản ứng dụng
  // =======================================================
  useEffect(() => {
    async function loadVersion() {
      try {
        const latest = await getLatestVersion();
        if (latest) setAppVersion(latest.version_code); 
      } catch (err) {
        console.error("Không thể tải version:", err);
      }
    }
    loadVersion();
  }, []);

  /* ---------------------------------
     ▶ Xử lý đăng nhập
  --------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Vui lòng nhập đầy đủ thông tin");

    try {
      setSubmitting(true);
      const data = await loginUser({
        email,
        password,
        device_fingerprint: getOrCreateDeviceFingerprint(),
        device_class: getDeviceClass(),
        app_audience: "erp",
      });

      clearLessonCacheSessionKey();

      // 🔐 Token + user info
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user || {}));
      if (data.modules) localStorage.setItem("modules", JSON.stringify(data.modules));
      if (data.permissions)
        localStorage.setItem("permissions", JSON.stringify(data.permissions));

      // 💾 Remember Me (Mã hoá AES)
      if (rememberMe) {
        localStorage.setItem("remember_email", email);
        localStorage.setItem(
          "remember_password",
          CryptoJS.AES.encrypt(password, SECRET_KEY).toString()
        );
      } else {
        localStorage.removeItem("remember_email");
        localStorage.removeItem("remember_password");
      }

      toast.success("Đăng nhập thành công!");
      try {
        sessionStorage.removeItem(DESKTOP_UNREAD_REMINDER_KEY);
      } catch (_) {
        /* ignore */
      }
      registerSocketUser(data.user.id);

      setTimeout(() => {
        if (window.innerWidth < 768) navigate("/m/home");
        else navigate("/hrm/dashboard");
      }, 800);
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.message;
      if (code === "TEACHER_DEVICE_LIMIT") {
        toast.error(
          msg ||
            "Đã đủ thiết bị cùng loại (1 máy tính + 1 điện thoại). Liên hệ quản trị để duyệt hoặc gỡ thiết bị cũ."
        );
      } else if (code === "DEVICE_FINGERPRINT_REQUIRED") {
        toast.error(msg || "Thiết bị chưa định danh. Vui lòng bật cookie/localStorage hoặc dùng trình duyệt khác.");
      } else if (code === "APP_MAINTENANCE") {
        const detail = err.response?.data?.maintenance_message;
        toast.error(detail ? `${msg}: ${detail}` : msg || "Hệ thống đang bảo trì");
      } else if (code === "APP_AUDIENCE_DENIED") {
        toast.error(
          msg ||
            "Tài khoản giáo viên đối tác vui lòng đăng nhập qua IMA Lesson (app.ima-lesson.com)."
        );
      } else {
        toast.error(msg || "Đăng nhập thất bại");
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ==========================================================
     📱 UI MOBILE
  ========================================================== */
  if (isMobile) {
    return (
      <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#ffe5e5] via-white to-white px-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mb-10"
        >
          <img src={logo} alt="IMA Việt Nam" className="h-14 drop-shadow-sm" />
        </motion.div>

        <motion.form
          id="login-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-5 w-full max-w-sm"
        >
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              placeholder="Email của bạn"
              className="w-full rounded-xl pl-11 pr-4 py-3 bg-gray-50 text-lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Mật khẩu + Toggle */}
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />

            <input
              type={showPassword ? "text" : "password"}
              placeholder="Mật khẩu"
              className="w-full rounded-xl pl-11 pr-10 py-3 bg-gray-50 text-lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {/* 👁 Nút hiện/ẩn */}
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between w-full mt-2">

            {/* 🔘 Remember Me */}
            <div
              className="flex items-center gap-2 select-none cursor-pointer"
              onClick={() => setRememberMe(!rememberMe)}
            >
              <div
                className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all 
                  ${
                    rememberMe
                      ? "bg-gradient-to-r from-[#ff4b2b] to-[#ff416c] border-transparent"
                      : "border-gray-400 bg-white"
                  }`}
              >
                {rememberMe && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              <span className="text-sm text-gray-700">Ghi nhớ đăng nhập</span>
            </div>

            {/* 🔗 Forgot Password */}
            <Link to="/forgot-password" className="text-rose-500 text-sm hover:underline">
              Quên mật khẩu?
            </Link>
          </div>
        </motion.form>

        {/* Nút đăng nhập + Link đăng ký */}
        <div className="fixed bottom-0 left-0 right-0 bg-white p-5 shadow-lg pb-20">
          <button
            type="submit"
            form="login-form"
            className="w-full bg-gradient-to-r from-[#ff4b2b] to-[#ff416c] text-white py-3.5 rounded-full text-base"
          >
            Đăng nhập
          </button>

          <div className="text-center text-sm mt-3">
            <span className="text-gray-500">Bạn chưa có tài khoản?</span>{" "}
            <Link to="/register" className="text-rose-500">
              Đăng ký tài khoản
            </Link>
          </div>
        </div>

        {/* Footer nhỏ – Powered by */}
        <div className="fixed bottom-0 left-0 right-0 pb-3">
          <p className="text-center text-xs text-gray-400">
          Powered by <span className="font-semibold">Hameno Software</span> • Version {appVersion?.slice(1,) || "0.0.0"}
          </p>
        </div>
      </div>
    );
  }

  /* ==========================================================
     🖥 UI DESKTOP — dùng AuthShell đồng bộ register / forgot
  ========================================================== */
  return (
    <AuthShell
      eyebrow="Đăng nhập"
      title="Chào mừng trở lại"
      subtitle="Nhập email và mật khẩu để tiếp tục làm việc."
      footer={
        <p className="text-center text-sm text-stone-500">
          Bạn chưa có tài khoản?{" "}
          <Link to="/register" className="font-semibold text-[#8f1219] hover:underline">
            Đăng ký tài khoản
          </Link>
        </p>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-stone-700">Email</label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
              size={17}
            />
            <input
              type="email"
              autoComplete="username"
              placeholder="name@example.com"
              className={authInputWithIconClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-stone-700">Mật khẩu</label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
              size={17}
            />
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`${authInputWithIconClass} pr-11`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 transition hover:text-stone-600"
              aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            className="flex items-center gap-2 select-none"
            onClick={() => setRememberMe(!rememberMe)}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-md border transition-all ${
                rememberMe
                  ? "border-transparent bg-[#8f1219]"
                  : "border-stone-300 bg-white"
              }`}
            >
              {rememberMe && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
            <span className="text-sm text-stone-600">Ghi nhớ đăng nhập</span>
          </button>

          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-[#8f1219] transition hover:underline"
          >
            Quên mật khẩu?
          </Link>
        </div>

        <button type="submit" disabled={submitting} className={authPrimaryBtnClass}>
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Đang đăng nhập...
            </>
          ) : (
            <>
              Đăng nhập
              <ArrowRight size={17} className="transition group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}
